import { env } from '@/config/env';

// Per-browser switches for encounter features that ship dark (PROD-2743, the 1 Oct demo build).
// Every switch is OFF by default and is turned on only in the browser that asks for it:
//   ?ef=labsPanel      turn a switch on (remembered in this browser)
//   ?ef=-labsPanel     turn it off again
// A switch never changes the default path for anyone who hasn't turned it on, except the DEFAULT_ON ones below
// (readyList, dayAgenda), which read ON until this browser turns them off with `-name`.
// Storage can be missing or blocked (private window, blocked site data), so every read and write
// is guarded and a failure reads as OFF.

// `phrasePieces` — smart-phrase PIECES (pick-lists, blanks, chart links) in the A&P narrative.
// OFF: a phrase whose definition carries any piece is not offered; plain phrases work as before.
// Goes on only once the React → Flutter → React round trip has passed on dev.
// `planSave` — [PROD-2949] the non-prescriber plan saves: the allied-health Plan step (MS-1) and, with
// `frameV2` also on, the nurse's Nursing plan block. OFF: the Plan step keeps its "ships next"
// placeholder and the nurse sees no plan block. Goes on once AddNonPrescriberPlanTables.sql is applied
// and the Scrips.Patient endpoint is deployed on dev.
//
// `readyList` — the physician home as the "ready list" (Your day, Today, Needs you, Inbox). ON BY DEFAULT since
// 15 Oct (Antonin: the home "as much as possible as the prototype"); `?x=-ready-list` turns it off in this browser and
// brings back the worklist home, `?x=ready-list` turns it on again. The one switch here that defaults on: see DEFAULT_ON.
//
// `dayAgenda` — the calendar's Day view as the Practice calendar's agenda table (a local component in place of the Signal DS
// day list). ON BY DEFAULT since 15 Oct (Antonin: "day view is too apart from PM"); `?x=-day-agenda` turns it off in this
// browser and brings back the DS day list for comparison, `?x=day-agenda` turns it on again.
//
// `noteSections` — DEVELOPER ONLY. Frame v2's note shows one section at a time instead of the one
// continuous scroll. Design desk ruling D-64: one scroll is the ONLY mode clinicians see; this switch
// exists for developers comparing the two, and nothing in the product turns it on.
//
// `encounterPolish` — the 14 Oct encounter polish from the clinician demo (showcase look, ELLMS build): the
// HPI field reads quiet at rest (no border, hover hairline, blue ring on focus, all text visible). Look only;
// OFF renders the encounter exactly as before. Read once per mount by `encounter-route.tsx`. Also switched by
// `?x=encounter-polish` / `?x=-encounter-polish` (an alias).
//
// `frameV2`, `planSave`, `phrasePieces` — ON BY DEFAULT for the ENCOUNTER since 19 Oct (Antonin; Andrew confirmed 21 Oct): the started
// encounter is the frame v2 one long page, with the plan save and the phrase pieces, and there is no Welcome page (removed 19 Oct).
// `?ef=-planSave` and `?ef=-phrasePieces` turn one off in this browser. Until 19 Oct these three were implied by the
// `welcome` switch (ON by default since 16 Oct); a browser that remembered `-welcome` is unaffected (the name is no longer known).
//
// `frameV2` EVERYWHERE since 2 Nov (Samer: take Frame 1 out of what clinicians see): frame v2 is ON on every screen for everyone,
// the encounter AND the app screens (Today, the chart tables, settings); `ownSwitchOnly` no longer keeps the app screens on
// Frame 1. A browser's own off (`?ef=-frameV2`, or one remembered from before) is IGNORED, because browsers had drifted apart on
// it. The one way back to Frame 1 is the developer fallback `?x=frame-1` (`?x=-frame-1` returns to v2), honoured only on the
// dev host and local builds (FRAME_ONE_HOSTS) and remembered in this browser under FRAME_ONE_KEY. Frame 1's code stays until it
// is deleted screen by screen.
//
// `assignCarePlan` — [PROD-3206] the physician's "Assign care plan" action in the A&P Plan area: pick a care model,
// Confirm, and the patient is enrolled (Workflows C2). OFF by default; `?x=assign-care-plan` turns it on in this browser,
// `?x=-assign-care-plan` turns it off. OFF renders the A&P exactly as before and makes no care-model call.
// Where the deploy sets VITE_ASSIGN_CARE_PLAN_DEFAULT_ON=true (dev only, ELLMS 23 Oct 2026) it reads ON until this browser
// turns it off with `?x=-assign-care-plan`, like the DEFAULT_ON switches.
//
// `referrals` - [PROD-1395 / PROD-3336] the physician's referral to a clinic SPECIALITY in the A&P, with an optional practitioner, and the
// "Referral" card on the allied-health pre-encounter. OFF by default; `?x=referrals` or `?ef=referrals` turns it on in this
// browser, `-referrals` turns it off. OFF renders the A&P and the pre-encounter exactly as before and makes no referral call.
// Where the deploy sets VITE_REFERRALS_DEFAULT_ON=true (dev only, 24 Oct 2026) it reads ON until this browser turns it off
// with `?x=-referrals`, like the DEFAULT_ON switches.
// `patientReported` - the "Patient-reported" section of the patient summary: the pre-visit questionnaires the patient
// submitted (PHQ-9 first), score as stored. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every switch ON on dev), OFF elsewhere; `?x=patient-reported` turns it on in this browser,
// `-patient-reported` turns it off. OFF renders the summary exactly as before and makes no QuestionBank call.
// `signReason` - a refused Sign also says WHY in a line next to the Sign button (the ~4 s toast stays). ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every switch ON on dev), OFF elsewhere;
// `?x=sign-reason` turns it on in this browser, `-sign-reason` off. OFF renders the sign exactly as before.
// `analytics` - the Analytics page (/analytics, a rail tile): the clinic's Programmes and Population analytics, ported from the
// clinician prototype with its own seeded clinic population (src/analytics-hub). OFF by default; `?x=analytics` turns it on in this
// browser, `-analytics` off. It also needs the deploy flag VITE_ANALYTICS, on only in the dev deploy and local dev (never prod):
// without it, `?x=analytics` does nothing (analytics-switch.ts). OFF shows no tile, and the route says the page is not
// available. It makes no API call either way.
// `growthChart` - the scheduling Vitals tab shows the pediatric growth chart (DS GrowthChart) under the vitals for a
// patient aged 19 or under with a sex on file, as Flutter's `vitals_tab.dart:320` does. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every switch ON on dev), OFF elsewhere; `?x=growth-chart`
// turns it on in this browser, `-growth-chart` off. OFF renders the tab exactly as before. Same switch name as pm-react.
//
// `pvsVisitNotes` - the Post Visit Summary dialog shows the signed template answers ("Visit notes", `templateNarrative`)
// as the printed PDF already does. Read-only. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every switch ON on dev), OFF elsewhere; `?x=pvs-visit-notes` turns it on in this browser,
// `-pvs-visit-notes` off. OFF renders the summary exactly as before.
// `slashInsert` - a backslash and a trigger word in the A&P Orders block (\med, \dx, \vax, \lab, \fav) bring up ONE kind of
// item to add, instead of the mixed search. It is a new line above "Add order"; the two existing lines stay. OFF by default;
// `?x=slash-insert` turns it on in this browser, `?x=-slash-insert` turns it off. OFF renders the A&P exactly as before and
// makes no new call. `\ref` (clinic specialities) is offered only with `referrals` on too. Where the deploy sets
// VITE_SLASH_INSERT_DEFAULT_ON=true (dev only, 24 Oct 2026) it reads ON until this browser turns it off with `?x=-slash-insert`.
// `tasks` - task management R1 (23 Oct): the "Tasks" rail entry and page (My tasks, Team queue, New task) over the
// Scrips.Patient task store, with care-model review steps as tasks. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every switch ON on dev), OFF elsewhere; `?x=tasks` turns it on in this browser,
// `?x=-tasks` off. OFF shows no rail entry, and /tasks says the page is off; no task call is made.
// `chartIntake` - PROD-3204 (Andrew): in the patient chart, Documents > "Add document" goes through Document Intake as PM does
// (upload, reading, review, confirm) instead of the plain file popup. Shown only with the deploy flag VITE_DOCUMENT_INTAKE too
// (`isChartIntakeOn`). ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every switch ON on dev), OFF elsewhere; `?x=chart-intake` turns it on in this browser, `?x=-chart-intake` off. OFF = the popup, as before.
// `intakeType` - PROD-3339: every Document Intake inbox row gets a type picker; the classifier's suggestion is pre-selected and
// marked "Suggested"; Confirm type saves the pick (and reads a lab report's values). Needs the intake view on (`?x=intake` or
// `?x=chart-intake`, `isIntakeTypePickerOn`). ON by default since 25 Oct (Andrew): wherever the intake inbox shows; `?x=-intake-type`
// turns it off in this browser (remembered), `?x=intake-type` on again. OFF = as before.
// `decisionButtons` - the Scrips Intelligence Next step card's physician decision buttons (Add to note, Not indicated, Defer,
// Already done; POST /api/intelligence/decisions, an audit record only, nothing written to the chart). OFF by default: the
// engine's decisions route is not yet stamped (Codex review + golden re-run pending, engine owner 1 Nov). `?x=decision-buttons`
// turns it on in this browser, `?x=-decision-buttons` off. OFF shows the card read-only (and any decision already recorded).
// `ask` - the Scrips Intelligence rail's Ask box sends the clinician's question to the engine's state interpreter (Samer,
// 4 Nov 2026). OFF by default until Antonin's verdict on the Ask card (D-79) and the owner's ruling on which facts the agents
// may cite; `?x=ask` turns it on in this browser, `?x=-ask` off. OFF = Ask drawn disabled, as before.
// `intelWarm` - Today warms the clinician's NEXT visit's Explain (Heart & vessels) with the clinician's own token, so the
// visit opens without the AI check's slow first look (Samer, 4 Nov 2026; engine join, intel-warm.ts). Nothing is shown or
// written. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=intel-warm` on, `?x=-intel-warm` off.
// `wearables` - the patient's own device (wearable) trends in the encounter's chart column (encounter owner ruling, 30 Oct).
// `useWearableTrends` reads nothing unless this is on. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov),
// OFF elsewhere; `?x=wearables` turns it on in this browser, `?x=-wearables` hides it. A 403 (no consent) hides the section.
// The server's wearable-sharing consent is the control; this switch is not.
// `staffChat` - SR-14 staff chat MVP (demo 4 Nov 2026): the Team chat dock at the bottom right of every page (collapsed
// bar with the unread total, open with the conversations and the open one), the Team card on the home, the
// /messages/:threadId deep link, and the Notifications hub connection for live delivery. ON BY DEFAULT on the dev hosts only
// (DEV_DEFAULT_ON; Andrew, 3 Nov), OFF elsewhere; `?x=-staff-chat` brings back no chat (remembered), `?x=staff-chat` turns it
// on again. OFF shows none of it, opens no hub connection and makes no staff-chat call.
// `staffChatPatient` - SR-14 patient threads (Samer's call pending: patient-data access and audit): "Message a colleague"
// on the patient chart and encounter headers and the patient card in a thread. Needs `staffChat` too. OFF by default;
// `?x=staff-chat-patient` on, `?x=-staff-chat-patient` off.
// `deviceCardCatalog` - the device data card is Antonin's own DeviceTrendsCard from @scripsteam/signal (Samer, 4 Nov 2026:
// "a true tokenized, machine-readable, declarative wearable data component from the design catalogue"; D-79), fed by
// the same hook through a thin adapter; the app draws none of its look. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON),
// OFF elsewhere; `?x=device-card-catalog` turns it on in this browser, `?x=-device-card-catalog` off. OFF = the PA card, as before.
// `previsitHidePathway` - the pre-visit's right column (Care pathway, Note) goes; the main column takes its width (Antonin,
// approved by Samer, 2 Nov: old design). Nothing is deleted and no data changes; the started encounter's drawer keeps its own
// Care pathway and Note. OFF by default; `?x=previsit-hide-pathway` turns it on in this browser, `?x=-previsit-hide-pathway` off.
// `hidePrevisitMeds` - hides the pre-visit "Active medications" list (read-only rows, no actions; the medicines are already
// in the left rail; Samer 2 Nov: "it needs to go"). OFF by default; `?x=hide-previsit-meds` turns it on in this browser,
// `?x=-hide-previsit-meds` off. Only the pre-visit list hides: the medication reads stay (the A&P and the wrap-up eRx use them).
// `previsitIntel` - the pre-visit's right column is the encounter's own Scrips Intelligence rail (phase "Pre-visit": Next best
// action, Explain), open on arrival, so the right column does not change between pre-visit and encounter (Antonin, 2 Nov,
// approved). It takes the place of Care pathway / Note, so it works with or without `previsitHidePathway`. No new read.
// OFF by default; `?x=previsit-intel` turns it on in this browser, `?x=-previsit-intel` off.
// `previsitNoCc` - the pre-visit shows no Chief complaint block for a physician or a nurse (Samer and Andrew, 2 Nov; Antonin
// agreed): the visit type and the reason for visit already say it, and both still enter it in the encounter's HPI step. Kept for
// allied health, which has no HPI step and cannot sign without a chief complaint (the server's sign refuses), until a referral
// note carries the reason. Display only: the record, its save and the sign rule are unchanged. OFF by default; `?x=previsitNoCc`
// (or `?x=previsit-no-cc`) turns it on in this browser, `?x=-previsitNoCc` off.
//
// `railV3` - the left patient summary rail drawn to Antonin's Claude Design handoff and LEFT-PANEL.md @f28d00c1 (2 Nov):
// sections in the spec's order with sentence-case titles and counts, every clinical section open on load (Occupation and
// Education folded), Resolved problems its own section, numbers at the row's end, the row icon bar and hover card.
// Look only: same sections, data, actions and handlers. OFF by default; `?x=rail-v3` turns it on in this browser,
// `?x=-rail-v3` off. OFF renders the rail exactly as before. Read once per mount by `patient-summary-rail.tsx`.
//
// DEV DEFAULTS (Andrew, 3 Nov, for the demo): `previsitHidePathway`, `hidePrevisitMeds`, `previsitIntel`, `previsitNoCc`,
// `railV3`, `previousNotes` and `analytics` read ON on the dev hosts only (the same FRAME_ONE_HOSTS: dev-practitioner-v2 and
// local builds), until this browser turns one off with its negative switch (`?x=-previsit-intel`, `?x=-rail-v3` and so on; the
// positive switch turns it on again). On every other host they stay OFF until a browser turns them on, so a later prod deploy
// does not change by itself. `analytics` also still needs its deploy flag VITE_ANALYTICS (isAnalyticsOn). See DEV_DEFAULT_ON.
// Added the same evening (Andrew: "in the demo we shouldn't add things to show the new features"): `tasks`, `chartIntake`,
// `intake`, `patientReported`, `signReason`, `pvsVisitNotes`, `growthChart` and `cohorts`. `intake`, `chartIntake`
// and `cohorts` still need their deploy flags (VITE_DOCUMENT_INTAKE, VITE_COHORT_ENROLMENT). `wearables` too (Andrew, 3 Nov):
// its 403 on dev is the consent check, and a 403 hides the section. Not added: `noteSections`
// (developer only), `decisionButtons` (its POST /api/intelligence/decisions route is not stamped yet, so dev would start writing
// to it once the Intelligence 403 is fixed), `encounterPolish` (its quiet HPI field outranks the `signalLook` textarea),
// `labsPanel` and `docViewer` (no screen reads them).
//
// `signalLook` - the shared look of Antonin's catalog (Section 1, primitives and chrome; LEFT-PANEL/ANATOMY @f28d00c1, 3 Nov):
// 14/24 body, 32 high controls and 40 high table rows, quiet red danger button, overlay shadow on menus, tooltip 8 x 8,
// default cursor on disabled controls, thin scrollbars, 1.75 non-scaling icon stroke, frame inset 8, top bar search 240.
// Look only: no markup, handler or data changes. It sets `data-look="signal"` on <html> (`src/app/signal-look.ts`) and every
// rule lives in `src/styles/showcase-signal.css` under that attribute, so OFF is the screen as before. ON BY DEFAULT on the
// dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov: every change ON on dev), OFF elsewhere; `?x=-signal-look` shows the old look,
// `?x=signal-look` turns it on again.
// `signalPrevisit` - the pre-visit screen (Frame V2) drawn to Antonin's catalog "Section 3 - Pre-visit" (scrips-signal-ds @f28d00c1,
// 3 Nov): every card a hairline 12-radius white card with 24 side padding, its items 20 above and below, one neutral 32 icon tile
// each joined by a 1 px line, the Nurse note before Vitals, the reason as a thin grey inner card, vitals as grey inner tiles,
// the rows of Previous notes as grey inner-card rows, and loading as skeleton lines. Look only: same cards, data, handlers,
// wording and permissions. It sets `data-previsit="signal"` on the pre-visit main column and every rule lives in
// `src/styles/showcase-previsit.css` under that attribute. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov),
// OFF elsewhere; `?x=-signal-previsit` is today's pre-visit, `?x=signal-previsit` turns it on again. Read once per mount.
//
// `wrapUpV3` - the encounter Wrap-up step drawn to Antonin's catalog (Section 6; WrapUpView / WrapUpParts @f28d00c1, 3 Nov): cards
// without a shadow, a 4 high progress track drawn from 0, the problem list and follow-up in sentence case on the type scale, one
// [- n +] follow-up stepper, the instructions as a labelled line, the invoice block and the pending order rows on the catalog's
// sizes. Look only: the same data, hooks, handlers and order (wrap-up-view.tsx sets `data-wrap-up-v3`; every rule lives in
// `src/styles/showcase-wrap-up.css` under it). ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere;
// `?x=-wrap-up-v3` shows the old wrap-up, `?x=wrap-up-v3` turns it on again.
//
// `encounterLatest` - Section 4, the encounter middle: the visit note on Antonin's catalog look (EncounterFixes VisitNote, @f28d00c1,
// 3 Nov): blocks 16 apart with a hairline and no shadow or icon tile, one 48 line per item with a 192 label column, a label's
// qualifier one level down, a label that repeats its group title kept for screen readers only. Display only: no handler or data
// changes. It sets `data-enc-latest="on"` on <html> (`src/app/encounter-latest-look.ts`); every rule lives in
// `src/styles/showcase-encounter-latest.css` under that attribute, and the label parts are `structured-note/label-parts.tsx`.
// ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-encounter-latest` shows the old note.
//
// `diagnosesBlock` - PROD-3398, the encounter Diagnoses block as Antonin's catalog draws it (Claude Design card 29a; scrips-signal-ds
// feat/signal @62cb3650, AssessmentPlanView `AssessmentPart`, "Changed vs dev (3 Nov)"): an empty Diagnoses block is the Add
// diagnosis line only (no "No diagnoses added" line), and an empty part's drop outline is solid, not dashed. The search, the
// cards, the Primary / Secondary picker and "Joins problem list at sign" are today's. Every template's Diagnoses section (the
// A&P view draws it). ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-diagnoses-block` is today's block.
//
// `ambientWindow` - the ambient scribe as Antonin's catalog draws it (scrips-signal-ds feat/signal @62cb3650; SCREEN-MAP row "Ambient
// (Start listening card, window)": components/AmbientMic, AmbientScribe, pages/WorkspacePage): a "Start listening" card on its own
// row above the note, right-aligned (it replaces the small page-header mic), and the session in a floating window that opens
// bottom-centre over the rail (ambient/ambient-window.tsx). The window's body is today's AmbientScribePanel, the same element and
// props: consent is asked at Start only, and nothing enters the note until a clinician accepts it. ON BY DEFAULT on the dev hosts
// only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-ambient-window` is today's panel in the rail with the header mic.
//
// `ambientOverlay` - Samer's standing ruling (4 Nov): "ambient shouldn't be in the intelligence rail. Use the popup overlay". The
// physician's ambient session is ONLY the floating window (ambient/ambient-overlay.ts): never in the Intelligence rail, even in a
// browser that turned `ambientWindow` off; the header mic stays as an entry point beside the Start listening card, and the window
// opens by itself when listening starts from any entry point. Nurse: no ambient at all. The window body is unchanged (today's
// AmbientScribePanel and Andrew's endpoints). ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere;
// `?x=-ambient-overlay` is the `ambientWindow` behaviour exactly as before.
//
// `medActions` - Samer's Loom (3 Nov): "change, refill, stop, and then it goes to the encounter part". In a started encounter, for
// a user who may order medications, a chart medication row offers Continue · Refill · Change (each opens today's medication
// dose dialog in the A&P Plan, seeded with that drug and schedule, saved as today's plan order; Refill is a new order for the
// same drug and dose until a PostRefill hook exists) beside Edit and Stop; the A&P Active Medications row's Change, Refill and
// Discontinue (= today's Stop) are wired to the same handlers. Same APIs, no new backend. ON BY DEFAULT on the dev hosts only
// (DEV_DEFAULT_ON), OFF elsewhere; `?x=-med-actions` is today's rows (Edit, Stop) and the disabled A&P buttons.
//
// `dxSearchFirst` - PROD-3423 (Samer's Loom, 3 Nov: "Add diagnosis, it's just an order set, it's not even showing the diagnosis"):
// the "Add diagnosis" line opened on the clinician's order sets. With the switch on it is the diagnosis search only, as the
// catalog's Diagnoses block 29a (@62cb3650: "search open (ICD-10 results)"): open, your diagnosis favourites; typed, your
// favourites then the ICD-10 catalogue. No order sets on this line (they stay on "Add order"). ON BY DEFAULT on the dev hosts
// only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-dx-search-first` is today's line with the order sets.
//
// `explicitDxLink` - PROD-3423 (Samer's Loom, 3 Nov: "why it's adding to the hypertension"): an order opened from Add order, a seed
// (ambient, the Prescriptions add, a chart medication) or a drop that is not on a diagnosis card used to pre-select the primary
// diagnosis, so it was linked to it unasked. With the switch on and 2+ diagnoses, that dialog opens with NO diagnosis selected,
// so its required DIAGNOSIS block asks (Save stays off until one is picked, as the dialog shell already enforces). With one
// diagnosis, a drop on a card, or an edit, the links are as before. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF
// elsewhere; `?x=-explicit-dx-link` is today's pre-selection.
//
// `rxSigWording` - Samer's review (3 Nov): the Prescription card's schedule read "Every day · 1 time a day", the same thing twice.
// With the switch on, an every-day order with a times-a-day count reads "Once a day" / "2 times a day" (no "Every day" before
// it); every other frequency reads as before. Display only (assessment-plan-view.tsx `medicationSchedule`). ON BY DEFAULT on the
// dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-rx-sig-wording` is the old wording.
//
// `dxOrderSplit` - Andrew (4 Nov): the diagnosis and the order searches are separate. The Add order line (and its \fav) lists
// order items only: no diagnosis rows, no \dx. The Add diagnosis line takes its own backslash triggers (the same parser,
// lib/slash-trigger.ts `DX_SLASH_KINDS`): \fav your diagnosis favourites, \sug the AI diagnosis suggestions (the existing
// GET /api/AISearch/Diagnosis, only where the clinic has AzureAI diagnosis; else it says it is not set up here), \set your
// order sets. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-dx-order-split` is today's lines.
//
// `planTitle` - Andrew (4 Nov): the Plan step's block was titled "Orders"; it reads "Plan", as the step tab above it. ON BY
// DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-plan-title` is "Orders".
//
// `dxBinInline` - Andrew's A&P bug list, item 6 (4 Nov): on the long page the diagnosis row's bin drew INSIDE the "Primary" control (the
// bridge pins the bin to the card's top-right corner, and the long-page row puts the 112 role control in that same corner). With it,
// the bin sits in the row after the role control. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere;
// `?x=-dx-bin-inline` is today's overlap.
//
// `oneOrderLine` - PROD-3399 (Samer, 3 Nov call: the Orders block "cannot have multiple components"; Antonin's list "Advisors: one
// instance"; Claude Design card 30): with `slashInsert` on, the Orders block draws ONE "Add order" line, and text that starts
// with "\" in it is the backslash search (ap-slash-add-line.tsx `ApOrderSlashAddLine`). Same searches, handlers and picks.
// ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-one-order-line` brings back the separate
// "Add with \" line above "Add order".
// `chartReadout` - Antonin's Chart readout (catalog ChartReadout, scrips-signal-ds feat/signal @166c3c35, 3 Nov): in a note line
// that takes phrases (`structured-note/inline-note-line.tsx`: the A&P narrative, the history notes, the wrap-up lines), typing "\"
// at the end of the text lists the patient's chart data (the catalogue lab sets that have results, and today's vitals); a pick
// inserts a read-only readout under the text, removed whole with its X. It is a chart piece (note-pieces.ts) marked `readout`, so
// it is saved in the same `notes` Delta and Flutter reads its text. Reads only what the line already has (`chartReads`). ON BY
// DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-chart-readout` keeps "\" as plain text.
//
// `prescriptionCard` - the physician Plan's medication orders as prescription cards (Antonin's catalog PrescriptionCard +
// EncounterDev personaBlocks code:ap, scrips-signal-ds @166c3c35, 3 Nov): after the Plan, a "Prescriptions" block with one grey
// inner card per drug (drug, schedule, edit, remove) and Add prescription. Same orders, payloads, dialogs and save path: edit opens
// today's dose dialog, remove is today's onRemoveOrder, Add prescription opens today's medication search. The other order types
// stay where they are. OFF BY DEFAULT everywhere since Andrew, 4 Nov ("no need of it as it's already in order"): the medications
// are order rows, as before #885; `?x=prescription-card` brings the cards back.
// Antonin's template decisions (3 Nov, catalog feat/signal @41ce9e29 / @64e69b6f) that are app changes, not template writes. Each
// is ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere:
// `physicianVitalsReadonly` - the physician's PE vitals block shows the nurse's readings read-only: no Edit / Add (History and
// Mark reviewed stay). `?x=-physician-vitals-readonly` brings back Edit.
// `planNoNcp` - the allied Today's plan no longer offers "Nutrition care process" (the dietitian's ADIME is asked once, in her
// template). A note that already picked it keeps it. `?x=-plan-no-ncp` offers it again.
// `formNameCase` - the pre-visit submitted questionnaire's name starts with a capital ("first time" reads "First time"); display
// only, the stored title is unchanged. `?x=-form-name-case` shows the name as stored.
//
// `visitNoteV2` - the visit note drawn as the catalog's encounter, `#/workspace` (WorkspacePage + EncounterDev, scrips-signal-ds
// @62cb3650, Antonin's list A, 3 Nov; PROD-3389, PROD-3390, PROD-3391, PROD-3395): one card per step with its icon tile and no
// S/O/A/P frame around it, the block head a title only, "Add note" after the content, no hairline between lines, 32 high
// chips, and a label cut at its first ':' with the full text in an info tooltip. Same data, hooks and handlers. It sets
// `data-visit-note-v2="on"` on <html> (`src/app/visit-note-v2-look.ts`); the look is `src/styles/showcase-visit-note.css`.
// ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov), OFF elsewhere; `?x=-visit-note-v2` shows today's note.
//
// Pre-demo cleanups (3 Nov call, Samer and Antonin). Each ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere:
// `homeSkeleton` - the home's Today card loads as three skeleton lines (the catalog's card loading state), not the brand spinner.
//   `?x=-home-skeleton` shows the spinner.
// `yourDayOff` - the home has no "Your day" strip (PROD-3382: "it pulls no data"). `?x=-your-day-off` shows it again.
// `docsBlockV2` - the encounter Documents block offers no "Nothing to record", and Add Order / Add document are full buttons
//   with an icon (PROD-3397). Same handlers. `?x=-docs-block-v2` shows the toggle and the icon buttons.
// `previsitBack` - the long page's header Back returns to the pre-visit (Andrew, 3 Nov: "I can't go back from encounter to the
// pre-encounter screen"). The step-by-step frame always had it; the long page left it out on 19 Oct, when it had no pre-visit.
// Same control and handler (`setStarted(false)`: no route change, no write). ON BY DEFAULT on the dev hosts only
// (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-previsit-back` hides it.
//
// `previsitHeader` - with rail v3, the pre-visit has ONE header, as the started encounter (Andrew, 3 Nov: "Duplication in headers in
// the pre encounter screen"): the page header carries the identity and the status control, the rail's person card keeps the contacts;
// and the pre-visit rail scrolls on its own ("no scroll in the left panel", PROD-3387). ON BY DEFAULT on the dev hosts only
// (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-previsit-header` shows today's pre-visit.
//
// `railFixes` - the left rail fixes from Antonin's 3 Nov review (Loom 1; PROD-3383, PROD-3384, PROD-3385, PROD-3386), on the rail-v3
// rail: no patient card at the top (the header already carries the identity); its contacts and their call links are the rail's own
// first section, and the card's language and importance move to the header (the subline and the photo); the status ("Checked in")
// opens the header subline instead of sitting beside the name; the rail has its right-edge hairline and every hairline runs the
// panel's full width; folding works on the pre-visit too and slides instead of reflowing (the rail keeps its open width inside and
// is clipped, as the catalog's ContextRail). Same data and actions. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF
// elsewhere; `?x=-rail-fixes` shows today's rail.
//
// `layoutFixes` - Antonin's PA layout fixes of 4 Nov (#current-active-team-scrips): the device data card's chart is capped where its
// axis labels draw at their own 12px (his chart scales its 300-wide drawing to the card's width); the Clinical note viewer opens as a
// centred DS Modal instead of a right-side drawer (same header, content and 640 width); the started visit's left rail draws its own
// right hairline (its white covered the column's inset-shadow divider); the Scrips Intelligence rail takes the room its right
// column has instead of overflowing it. Same data and actions. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF
// elsewhere; `?x=-layout-fixes` shows them as before.
//
// `templateReview` - Antonin's template review of 3 Nov (Hassan): the pre-visit's primary reads "Start encounter" for an in-progress
// visit too (PROD-3392); the reason headline one size smaller (20/28) with no location pin (PROD-3393); the step bar's S·O·A·P cells
// carry their section tints again (PROD-3402); a rail row fills the moment the pointer arrives (the hover item). It sets
// `data-template-review="on"` on <html> (`src/app/template-review-look.ts`) for the CSS parts. ON BY DEFAULT on the dev hosts only
// (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-template-review` shows today's screens.
//
// `vitalsComputedLive` - the vital cell's computed tiles (BMI, waist-to-hip ratio) compute live from what is typed while editing,
// with the Edit vitals dialog's rules, and at rest the ratio is derived from the stored hip and waist when none is stored (Samer,
// 3 Nov 20:44: "waist to hip computation is not working"). Display only. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON),
// OFF elsewhere; `?x=-vitals-computed-live` shows the stored value only, as before.
//
// `intelRailCatalog` - the Scrips Intelligence rail as Antonin's approved catalog rail (scrips-signal-ds feat/signal), consumed
// as React components from @scripsteam/signal (vendor/scripsteam-signal-0.1.2.tgz) and filled by thin adapters from the SAME
// engine hooks (src/encounter/intelligence/catalog-rail.tsx): his header, card shell, result rows, chip row, Ask and footer.
// Its Close Intelligence and reopen are `intelClose`'s (below). The notice gate, decisions (read-only unless `decisionButtons`)
// and every state line are unchanged. The rail renders inside main's SignalScope (the vendored Signal sheets, Antonin's tokens
// scoped to the rail). ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-intel-rail-catalog` shows
// today's rail (round 18r), unchanged.
//
// `nurseNoteCard` - the pre-visit "Notes from nurse" card (catalog PvNurseNote), wired read-only (Andrew, 4 Nov): the visit's Fall and
// Pain assessments as "Risk reassessment" rows (date only, the summary line), from the same EncounterMetaData reads the Risk card uses
// (one encounter per appointment, shared by the nurse and the physician). No author is named: an entry does not record who assessed
// it. The comment to the physician has no endpoint yet, so a nurse sees it marked "not saved yet" and nothing is sent. Nothing shows
// without data. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-nurse-note-card` shows today's pre-visit.
//
// `intelClose` - PROD-3388 (Antonin, 3 Nov: "Intelligence bar: keep it visible everywhere, never collapsed. It also needs a
// close"): the Scrips Intelligence rail starts OPEN on the pre-visit and on the started encounter's long page; the X in its
// header closes it and the floating orb button bottom right reopens it (intelligence/rail-close.ts). ON BY DEFAULT on the dev
// hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-intel-close` shows the old rail (no close on the pre-visit; the long page
// starts collapsed to the orb button, with the DS chevron).
//
// `wrapUpList` - the Wrap-up block as Antonin's closing list (PROD-3400; catalog EncounterDev `DevWrapUp`, ANDREW-FIX-LIST-3NOV A10).
// First part: no "Before you sign" banner in the block (Antonin 3 Nov: "remove the before you sign"); the floating sign bar
// (#900, drawn with `visitNoteV2`) lists what is missing, so the banner goes only when both switches are on. Sign and its gates
// are unchanged. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere;
// `?x=-wrap-up-list` shows the banner as before.
//
// `nurseEncounterV3` - Antonin's batch 3, nurse Mariam's first visit (3 Nov). PROD-3404: Fall and Pain as the catalog's optional
// block (feat/signal @62cb3650 `EncounterDev.tsx` `OptionalBlock`; Claude Design cards 18 and 19): one row with the question and
// its chips, Yes opens the lines, a chevron folds them to a summary (structured-note/risk-optional-block.tsx). Every template with
// Fall / Pain. Same items and writes. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere;
// `?x=-nurse-encounter-v3` shows today's Fall and Pain lines.
//
// `terrain` - Terrain (Andrew, 4 Nov; from Samer's demo `?view=terrain`): a Terrain tab beside Encounter in the encounter's
// top tabs (`?tab=terrain`, in place, so the note and its autosave stay mounted). Body systems from the patient's real lab,
// vitals and problem data; where the record has nothing it says so, never made-up values. ON BY DEFAULT on the dev hosts only
// (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-terrain` hides the tab.
// `carePlanTab` - the encounter header's "Timeline" tab becomes "Care plan" (Andrew, 4 Nov): Samer's demo protocol doc from the
// record (C5 care plans + C4 step): Plan board and Trajectory. Same tab slot and `?tab=timeline` route, so the note stays mounted.
// ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-care-plan-tab` brings back the care-pathway Timeline.
// `referralHandoff` - the referral as Antonin's hand-off card (@scripsteam/signal ReferralHandoff, catalogue 41 sender / 07 receiver):
// the physician's wrap-up shows each referral he sends to the care team (from -> to, the track, Why, Asks), and the receiving
// practitioner's pre-visit shows the incoming one. Same data and endpoints as `referrals` (GET /api/Patients/{id}/referrals,
// the encounter's ReferralRequest plan items); nothing new on the wire. ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON),
// OFF elsewhere, so prod is unchanged; `?x=-referral-handoff` brings back today's cards.
//
// `intelAlwaysOpen` - both side columns of the encounter open on arrival and stay open on every tab (pre-visit, the started visit,
// wrap-up, Patient chart, Timeline, Documents): the left patient column 320 and Scrips Intelligence 400 (Samer, 4 Nov: "the right
// and left rail stays open and active for the pre encounter and encounter and other tabs"). The zoning is Antonin's (scrips-signal-ds
// feat/signal rulebook R-LAY-03, R-LAY-04, R-AI-01; catalog/src/shell/useFit.ts, fc326b6d): the centre never drops under 480 (the lock's amended floor); when
// it would, the nav rail folds first, then the left column, then Intelligence (src/encounter/encounter-zoning.ts). Close
// Intelligence and the left toggle are remembered for the page session. It replaces D-41's "one side panel at a time below 1280".
// ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere; `?x=-intel-always-open` brings back today's behaviour.
// `signalNurseEncounter` - the NURSE's encounter after Start to Antonin's Claude Design page 56 (Samer, 4 Nov, VPS demo; rules in
// src/encounter/nurse-encounter-signal/nurse-encounter-rules.ts): ROS Absent / Present only, no Stridor / Apnea / Choking, and no
// physical exam (her Objective keeps the vitals). Nurse only; the physician and allied health are unchanged. Same writes. ON BY
// DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-signal-nurse-encounter` shows today's.
// `chartTimeline` - the patient chart's lead tab is the Timeline (Samer's demo view; Andrew, 4 Nov): lanes over time (Visits,
// Labs, Imaging & studies, Medications, Plan & interventions) from existing reads, in place of the Overview. ON BY DEFAULT on
// the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-chart-timeline` shows the Overview.
// `teamInRail` - the Team chat opens from a "Team" tile in the left menu, under Analytics; the collapsed bar at the bottom of
// the screen is not drawn (Samer, 4 Nov: it sits on the content). The open panel is unchanged. Needs `staffChat`. ON BY
// DEFAULT on the dev hosts only (DEV_DEFAULT_ON), OFF elsewhere, so prod is unchanged; `?x=-team-in-rail` brings back the bar.
export type EncounterFlag =
  | 'frameV2'
  | 'labsPanel'
  | 'docViewer'
  | 'phrasePieces'
  | 'planSave'
  | 'noteSections'
  | 'readyList'
  | 'encounterPolish'
  | 'dayAgenda'
  | 'assignCarePlan'
  | 'intake'
  | 'referrals'
  | 'cohorts'
  | 'patientReported'
  | 'signReason'
  | 'pvsVisitNotes'
  | 'slashInsert'
  | 'tasks'
  | 'chartIntake'
  | 'intakeType'
  | 'growthChart'
  | 'decisionButtons'
  | 'ask'
  | 'intelWarm'
  | 'wearables'
  | 'staffChat'
  | 'staffChatPatient'
  | 'deviceCardCatalog'
  | 'previousNotes'
  | 'hidePrevisitMeds'
  | 'previsitHidePathway'
  | 'previsitIntel'
  | 'previsitNoCc'
  | 'railV3'
  | 'signalLook'
  | 'signalPrevisit'
  | 'wrapUpV3'
  | 'encounterLatest'
  | 'prescriptionCard'
  | 'oneOrderLine'
  | 'diagnosesBlock'
  | 'ambientWindow'
  | 'ambientOverlay'
  | 'dxOrderSplit'
  | 'planTitle'
  | 'dxBinInline'
  | 'medActions'
  | 'dxSearchFirst'
  | 'explicitDxLink'
  | 'rxSigWording'
  | 'visitNoteV2'
  | 'physicianVitalsReadonly'
  | 'planNoNcp'
  | 'formNameCase'
  | 'homeSkeleton'
  | 'yourDayOff'
  | 'docsBlockV2'
  | 'chartReadout'
  | 'previsitBack'
  | 'previsitHeader'
  | 'templateReview'
  | 'chartTimeline'
  | 'teamInRail'
  | 'analytics'
  | 'vitalsComputedLive'
  | 'railFixes'
  | 'layoutFixes'
  | 'intelRailCatalog'
  | 'wrapUpList'
  | 'intelClose'
  | 'terrain'
  | 'carePlanTab'
  | 'nurseNoteCard'
  | 'nurseEncounterV3'
  | 'intelAlwaysOpen'
  | 'signalNurseEncounter'
  | 'referralHandoff';

const KNOWN: readonly EncounterFlag[] = [
  'frameV2',
  'labsPanel',
  'docViewer',
  'phrasePieces',
  'planSave',
  'noteSections',
  'readyList',
  'encounterPolish',
  'dayAgenda',
  'assignCarePlan',
  'intake',
  'referrals',
  'cohorts',
  'patientReported',
  'signReason',
  'pvsVisitNotes',
  'slashInsert',
  'tasks',
  'chartIntake',
  'intakeType',
  'growthChart',
  'decisionButtons',
  'ask',
  'intelWarm',
  'wearables',
  'staffChat',
  'staffChatPatient',
  'deviceCardCatalog',
  'previousNotes',
  'previsitHidePathway',
  'hidePrevisitMeds',
  'previsitIntel',
  'previsitNoCc',
  'railV3',
  'signalLook',
  'signalPrevisit',
  'wrapUpV3',
  'encounterLatest',
  'prescriptionCard',
  'oneOrderLine',
  'diagnosesBlock',
  'ambientWindow',
  'ambientOverlay',
  'dxOrderSplit',
  'planTitle',
  'dxBinInline',
  'medActions',
  'dxSearchFirst',
  'explicitDxLink',
  'rxSigWording',
  'visitNoteV2',
  'homeSkeleton',
  'yourDayOff',
  'docsBlockV2',
  'chartReadout',
  'previsitBack',
  'previsitHeader',
  'templateReview',
  'intelClose',
  'wrapUpList',
  'terrain',
  'referralHandoff',
  'chartTimeline',
  'teamInRail',
  'analytics',
  'physicianVitalsReadonly',
  'planNoNcp',
  'formNameCase',
  'vitalsComputedLive',
  'railFixes',
  'layoutFixes',
  'intelRailCatalog',
  'carePlanTab',
  'nurseNoteCard',
  'nurseEncounterV3',
  'intelAlwaysOpen',
  'signalNurseEncounter',
];
const STORAGE_KEY = 'scrips.encounterFlags';
/** Switches that read ON until this browser turns them off; their "off" is remembered under OFF_KEY. */
const DEFAULT_ON: readonly EncounterFlag[] = ['readyList', 'dayAgenda', 'intakeType'];
/** Switches that read ON on the dev hosts only (FRAME_ONE_HOSTS), until this browser turns them off (Andrew, 3 Nov: the demo
 *  shows the new behaviour without `?x=`). Their "off" is remembered under OFF_KEY, like DEFAULT_ON. The test seam
 *  (`encounter-flags.pin-off.ts`) pins these off too, so a suite written for the old pre-visit keeps testing it. */
const DEV_DEFAULT_ON: readonly EncounterFlag[] = [
  // Samer, 4 Nov (demo): "ask is not working". Ask (#910) on by default on the dev hosts; `?x=-ask` turns it off.
  'ask',
  'intelWarm',
  'previsitHidePathway',
  'hidePrevisitMeds',
  'previsitIntel',
  'previsitNoCc',
  'railV3',
  'previousNotes',
  'chartTimeline',
  'teamInRail',
  'analytics',
  'signalLook',
  'signalPrevisit',
  'wrapUpV3',
  'encounterLatest',
  'tasks',
  'chartIntake',
  'intake',
  'patientReported',
  'signReason',
  'pvsVisitNotes',
  'growthChart',
  'cohorts',
  'wearables',
  'deviceCardCatalog',
  'oneOrderLine',
  'diagnosesBlock',
  'ambientWindow',
  'ambientOverlay',
  'medActions',
  'dxOrderSplit',
  'planTitle',
  'dxBinInline',
  'dxSearchFirst',
  'explicitDxLink',
  'rxSigWording',
  'visitNoteV2',
  'physicianVitalsReadonly',
  'planNoNcp',
  'formNameCase',
  'homeSkeleton',
  'yourDayOff',
  'docsBlockV2',
  'chartReadout',
  'previsitBack',
  'previsitHeader',
  'templateReview',
  'vitalsComputedLive',
  'railFixes',
  'layoutFixes',
  'staffChat',
  'intelRailCatalog',
  'intelClose',
  'wrapUpList',
  'nurseNoteCard',
  'nurseEncounterV3',
  'signalNurseEncounter',
  'terrain',
  'intelAlwaysOpen',
  // Samer, 4 Nov (demo): the Next best action buttons must work. POST /api/intelligence/decisions is live on dev (it validates
  // the GUID idempotency key, 4 Nov), so the buttons are ON on the dev hosts only; prod is unchanged.
  'decisionButtons',
  'carePlanTab',
  'referralHandoff',
];

/** The encounter reads these ON until this browser turns them off (see the note above). A test seam pins that default off so a
 *  suite written for the step-by-step frame keeps testing it (`encounter-flags.pin-off.ts`); module state, so
 *  `localStorage.clear()` cannot undo it. */
const ENCOUNTER_DEFAULT_ON: readonly EncounterFlag[] = ['frameV2', 'planSave', 'phrasePieces'];
let encounterDefaultOn = true;
/** Switches that read ON where the deploy sets their dev build flag (src/config/env.ts), until this browser turns them off.
 *  Read at call time, so a test can change `env`. */
function deployDefaultOn(flag: EncounterFlag): boolean {
  if (flag === 'assignCarePlan') return !!env.assignCarePlanDefaultOn;
  if (flag === 'referrals') return !!env.referralsDefaultOn;
  if (flag === 'slashInsert') return !!env.slashInsertDefaultOn;
  return false;
}
export function pinEncounterDefaultsForTests(on: boolean): void {
  encounterDefaultOn = on;
}
/** DEV_DEFAULT_ON only, for suites written before the 3 Nov dev defaults (`encounter-flags.dev-defaults-off.ts`); frameV2 and the
 *  other encounter defaults stay as they are. Module state, like `encounterDefaultOn`. */
let devDefaultsOn = true;
export function pinDevDefaultsForTests(on: boolean): void {
  devDefaultsOn = on;
}
const OFF_KEY = 'scrips.encounterFlagsOff';

/** The screens everyone sees, whatever a browser remembered (Samer, 4 Nov: "I need to log in and see the changes; this is what
 *  will happen in the demo"). His browser held an old `-readyList` / `-signalPrevisit` from earlier test links and showed the old
 *  Today and pre-visit while every other browser showed Antonin's. For these a browser's own off is IGNORED (as frameV2's is): the
 *  default (DEFAULT_ON / the dev defaults) alone decides, so a sign-in shows the same screens on every machine. */
export const IGNORE_BROWSER_OFF: readonly EncounterFlag[] = ['readyList', 'signalPrevisit'];
/** The developer fallback to Frame 1 (`?x=frame-1`), remembered in this browser; read only on FRAME_ONE_HOSTS. */
const FRAME_ONE_KEY = 'scrips.frameOne';
/** Where `?x=frame-1` is honoured: the deployed dev site and a local build. Every other host is always frame v2. */
const FRAME_ONE_HOSTS: readonly string[] = ['dev-practitioner-v2.scrips.com', 'localhost', '127.0.0.1'];

function frameOneHost(): boolean {
  return FRAME_ONE_HOSTS.includes(window.location.hostname);
}

/** Applies `?x=frame-1` / `?x=-frame-1` on a FRAME_ONE_HOSTS host; anywhere else the parameter is ignored. */
function applyFrameOneParam(params: URLSearchParams): void {
  if (!frameOneHost()) return;
  for (const entry of params.getAll('x')) {
    if (entry !== 'frame-1' && entry !== '-frame-1') continue;
    try {
      if (entry === 'frame-1') window.localStorage.setItem(FRAME_ONE_KEY, '1');
      else window.localStorage.removeItem(FRAME_ONE_KEY);
    } catch {
      // Storage unavailable: the fallback stays as it was (frame v2 unless already stored).
    }
  }
}

/** True only on a FRAME_ONE_HOSTS host whose browser asked for Frame 1 with `?x=frame-1`. */
function frameOneFallback(): boolean {
  if (!frameOneHost()) return false;
  try {
    return window.localStorage.getItem(FRAME_ONE_KEY) === '1';
  } catch {
    return false;
  }
}
/** True when this browser is on the Frame 1 dev fallback (applies any `?x=frame-1` first); drives the dev marker in the shell. */
export function isFrameOneFallbackOn(): boolean {
  if (typeof window === 'undefined') return false;
  applyEncounterFlagParams(window.location.search);
  return frameOneFallback();
}
/** Short `?x=` names that switch a flag (the ready list is asked for as `?x=ready-list`). */
// `?x=intake` (Document Intake, PROD-3204): shown only with the deploy flag VITE_DOCUMENT_INTAKE too (`isIntakeOn`).
//   ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov), OFF elsewhere; `?x=-intake` off.
// `?x=cohorts` (nurse cohort enrolment, PROD-3206): shown only with the deploy flag VITE_COHORT_ENROLMENT too (`isCohortEnrolmentOn`).
//   ON BY DEFAULT on the dev hosts only (DEV_DEFAULT_ON; Andrew, 3 Nov), OFF elsewhere; `?x=-cohorts` off.
const X_ALIASES: Readonly<Record<string, EncounterFlag>> = {
  'ready-list': 'readyList',
  'encounter-polish': 'encounterPolish',
  'day-agenda': 'dayAgenda',
  'assign-care-plan': 'assignCarePlan',
  intake: 'intake',
  referrals: 'referrals',
  cohorts: 'cohorts',
  'patient-reported': 'patientReported',
  'sign-reason': 'signReason',
  'pvs-visit-notes': 'pvsVisitNotes',
  'slash-insert': 'slashInsert',
  tasks: 'tasks',
  'chart-intake': 'chartIntake',
  'intake-type': 'intakeType',
  'growth-chart': 'growthChart',
  'decision-buttons': 'decisionButtons',
  ask: 'ask',
  'intel-warm': 'intelWarm',
  wearables: 'wearables',
  'staff-chat': 'staffChat',
  'staff-chat-patient': 'staffChatPatient',
  'device-card-catalog': 'deviceCardCatalog',
  'previous-notes': 'previousNotes',
  'previsit-hide-pathway': 'previsitHidePathway',
  'hide-previsit-meds': 'hidePrevisitMeds',
  'previsit-intel': 'previsitIntel',
  previsitNoCc: 'previsitNoCc',
  'previsit-no-cc': 'previsitNoCc',
  'rail-v3': 'railV3',
  'signal-look': 'signalLook',
  'signal-previsit': 'signalPrevisit',
  'wrap-up-v3': 'wrapUpV3',
  'encounter-latest': 'encounterLatest',
  'prescription-card': 'prescriptionCard',
  'one-order-line': 'oneOrderLine',
  'diagnoses-block': 'diagnosesBlock',
  'ambient-window': 'ambientWindow',
  'ambient-overlay': 'ambientOverlay',
  'dx-order-split': 'dxOrderSplit',
  'plan-title': 'planTitle',
  'dx-bin-inline': 'dxBinInline',
  'med-actions': 'medActions',
  'dx-search-first': 'dxSearchFirst',
  'explicit-dx-link': 'explicitDxLink',
  'rx-sig-wording': 'rxSigWording',
  'visit-note-v2': 'visitNoteV2',
  'home-skeleton': 'homeSkeleton',
  'your-day-off': 'yourDayOff',
  'docs-block-v2': 'docsBlockV2',
  'chart-readout': 'chartReadout',
  'rail-fixes': 'railFixes',
  'layout-fixes': 'layoutFixes',
  'previsit-back': 'previsitBack',
  'previsit-header': 'previsitHeader',
  'template-review': 'templateReview',
  'intel-close': 'intelClose',
  'wrap-up-list': 'wrapUpList',
  terrain: 'terrain',
  'referral-handoff': 'referralHandoff',
  'chart-timeline': 'chartTimeline',
  'team-in-rail': 'teamInRail',
  analytics: 'analytics',
  'physician-vitals-readonly': 'physicianVitalsReadonly',
  'plan-no-ncp': 'planNoNcp',
  'form-name-case': 'formNameCase',
  'vitals-computed-live': 'vitalsComputedLive',
  'intel-rail-catalog': 'intelRailCatalog',
  'care-plan-tab': 'carePlanTab',
  'nurse-note-card': 'nurseNoteCard',
  'nurse-encounter-v3': 'nurseEncounterV3',
  'intel-always-open': 'intelAlwaysOpen',
  'signal-nurse-encounter': 'signalNurseEncounter',
};

function readStored(key: string = STORAGE_KEY): Set<EncounterFlag> {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is EncounterFlag => KNOWN.includes(v as EncounterFlag)));
  } catch {
    return new Set();
  }
}

function writeStored(flags: Set<EncounterFlag>, key: string = STORAGE_KEY): void {
  try {
    window.localStorage.setItem(key, JSON.stringify([...flags]));
  } catch {
    // Storage unavailable: the switch simply stays as it was (OFF unless already stored).
  }
}

/** Applies any `ef` (and `x` alias) query parameters to the stored switches. Unknown names are ignored. */
export function applyEncounterFlagParams(search: string): void {
  const params = new URLSearchParams(search);
  applyFrameOneParam(params);
  const requested = params.getAll('ef');
  for (const entry of params.getAll('x')) {
    const off = entry.startsWith('-');
    const alias = X_ALIASES[off ? entry.slice(1) : entry];
    if (alias) requested.push(off ? `-${alias}` : alias);
  }
  if (requested.length === 0) return;
  const flags = readStored();
  const offs = readStored(OFF_KEY);
  let changed = false;
  for (const entry of requested) {
    const off = entry.startsWith('-');
    const name = (off ? entry.slice(1) : entry) as EncounterFlag;
    if (!KNOWN.includes(name)) continue;
    if (off) {
      flags.delete(name);
      offs.add(name);
    } else {
      flags.add(name);
      offs.delete(name);
    }
    changed = true;
  }
  if (changed) {
    writeStored(flags);
    writeStored(offs, OFF_KEY);
  }
}

/** True when this browser has turned the switch on; for a DEFAULT_ON switch, true unless this browser turned it off.
 *  frameV2 is ON on every screen whatever this browser stored; only the developer fallback `?x=frame-1` on a FRAME_ONE_HOSTS
 *  host turns it off (see the note above). planSave / phrasePieces read ON for the encounter only. With the encounter defaults
 *  pinned off (the test seam), frameV2 reads this browser's stored switch as before, `ownSwitchOnly` included. */
export function isEncounterFlagOn(flag: EncounterFlag, opts?: { ownSwitchOnly?: boolean }): boolean {
  if (typeof window === 'undefined') return false;
  applyEncounterFlagParams(window.location.search);
  if (flag === 'frameV2' && encounterDefaultOn) return !frameOneFallback();
  // A browser's own off is ignored for these (see IGNORE_BROWSER_OFF): their default decides.
  const browserOff = !IGNORE_BROWSER_OFF.includes(flag) && readStored(OFF_KEY).has(flag);
  if (!opts?.ownSwitchOnly && encounterDefaultOn && ENCOUNTER_DEFAULT_ON.includes(flag) && !browserOff) return true;
  const devDefault =
    !opts?.ownSwitchOnly && encounterDefaultOn && devDefaultsOn && DEV_DEFAULT_ON.includes(flag) && frameOneHost();
  if (DEFAULT_ON.includes(flag) || deployDefaultOn(flag) || devDefault) {
    return !browserOff;
  }
  return readStored().has(flag);
}
